import {
  Bell,
  CheckCheck,
  Flag,
  Mail,
  TrendingUp,
  UserCheck,
  Zap,
  type LucideIcon,
} from "lucide-react";
import type { TFunction } from "i18next";

// A workflow's mini node-graph is composed of typed nodes. Each type maps to an
// icon + a semantic color chip (matching the Figma thumbnails).
export type WorkflowNodeType =
  | "trigger"
  | "approval"
  | "notification"
  | "escalation"
  | "email"
  | "validation"
  | "end";

export const WORKFLOW_NODE_TYPES: WorkflowNodeType[] = [
  "trigger",
  "approval",
  "notification",
  "escalation",
  "email",
  "validation",
  "end",
];

export const WORKFLOW_NODE_CONFIG: Record<
  WorkflowNodeType,
  { Icon: LucideIcon; className: string }
> = {
  trigger: { Icon: Zap, className: "bg-primary text-white" },
  approval: { Icon: UserCheck, className: "bg-success text-white" },
  notification: { Icon: Bell, className: "bg-warning text-white" },
  escalation: { Icon: TrendingUp, className: "bg-info text-white" },
  email: { Icon: Mail, className: "bg-info text-white" },
  validation: { Icon: CheckCheck, className: "bg-info text-white" },
  end: { Icon: Flag, className: "bg-foreground text-background" },
};

export type WorkflowStatus = "active" | "draft" | "inactive";

export interface WorkflowSummary {
  id: string;
  title: string;
  module: string;
  /** Backend module key (e.g. "leave_management"), for chip coloring. */
  moduleKey?: string;
  status: WorkflowStatus;
  scope: string;
  runs: number;
  ownerInitials: string;
  nodes: WorkflowNodeType[];
}

export const buildWorkflowCategories = (t: TFunction) =>
  [
    { key: "leave", label: t("studio:workflows.leave_management") },
    { key: "asset", label: t("studio:workflows.asset_management") },
    { key: "overtime", label: t("studio:workflows.overtime_requests") },
  ] as const;

export type WorkflowCategoryKey = ReturnType<
  typeof buildWorkflowCategories
>[number]["key"];

// Status filter options for the "Your workflows" toolbar. The "all" sentinel
// maps to no `status` query param (Radix Select forbids an empty-string value).
// Wired to the backend `status` query param on GET /workflows/templates.
export const ALL_STATUSES = "all";

/** The `status` URL/API contract — values only, safe for `enumOf()`. */
export const WORKFLOW_STATUS_VALUES = [
  ALL_STATUSES,
  "active",
  "draft",
  "inactive",
] as const;

export const buildWorkflowStatusFilters = (
  t: TFunction,
): { label: string; value: string }[] => [
  { label: t("studio:workflows.all_statuses"), value: ALL_STATUSES },
  { label: t("studio:workflows.active"), value: "active" },
  { label: t("studio:workflows.draft"), value: "draft" },
  { label: t("common:status.inactive"), value: "inactive" },
];

// Sort options — DB-backed columns only (run counts aren't sortable server-side).
// Wired to the backend `sort` query param; "newest" is the server default.
/** The `sort` URL/API contract — values only, safe for `enumOf()`. */
export const WORKFLOW_SORT_VALUES = [
  "newest",
  "oldest",
  "name_asc",
  "name_desc",
] as const;

export const buildWorkflowSortOptions = (
  t: TFunction,
): { label: string; value: string }[] => [
  { label: t("studio:workflows.newest"), value: "newest" },
  { label: t("studio:workflows.oldest"), value: "oldest" },
  { label: t("studio:workflows.name_a_z"), value: "name_asc" },
  { label: t("studio:workflows.name_z_a"), value: "name_desc" },
];

export const DEFAULT_WORKFLOW_SORT = "newest";

// The "Your workflows" listing filter state (status + sort), edited inside the
// FiltersModal and applied to the templates list query.
export interface WorkflowFilters {
  status: string;
  sort: string;
}

export const DEFAULT_WORKFLOW_FILTERS: WorkflowFilters = {
  status: ALL_STATUSES,
  sort: DEFAULT_WORKFLOW_SORT,
};

export const getCategoryLabel = (key: string, t: TFunction): string =>
  buildWorkflowCategories(t).find((category) => category.key === key)?.label ??
  t("studio:workflows.workflows");

// ─── Backend wiring ───────────────────────────────────────────────────────────

// Studio category key ↔ backend WorkflowModule.
export const CATEGORY_TO_MODULE: Record<WorkflowCategoryKey, string> = {
  leave: "leave_management",
  asset: "asset_management",
  overtime: "overtime_requests",
};

export const getModuleForCategory = (key: string): string =>
  CATEGORY_TO_MODULE[key as WorkflowCategoryKey] ?? "leave_management";

// Backend WorkflowModule → Studio category key (reverse of CATEGORY_TO_MODULE).
export const MODULE_TO_CATEGORY: Record<string, WorkflowCategoryKey> =
  Object.fromEntries(
    (Object.entries(CATEGORY_TO_MODULE) as [WorkflowCategoryKey, string][]).map(
      ([key, module]) => [module, key],
    ),
  );

export const getCategoryForModule = (module: string): string =>
  MODULE_TO_CATEGORY[module] ?? "leave";

// Short uppercase module chip shown on the workflow card.
export const MODULE_CHIP_LABEL: Record<string, string> = {
  leave_management: "LEAVE",
  asset_management: "ASSET",
  overtime_requests: "OVERTIME",
  time_attendance: "ATTENDANCE",
  offboarding: "OFFBOARDING",
};

// Chip color per module (brand illustration colors, inline — not palette tokens).
export const MODULE_CHIP_COLOR: Record<string, string> = {
  leave_management: "#db338c",
  asset_management: "#6438ff",
  overtime_requests: "#f97316",
  time_attendance: "#3b82f6",
  offboarding: "#111827",
};

// Backend list item shape (WorkflowListItemDto) returned by GET /workflows/templates.
export interface IStudioWorkflowListItem {
  id: number;
  name: string;
  module: string;
  status: WorkflowStatus;
  scopeType: string;
  scopeNames: string[];
  createdAt: string;
  hasUnpublishedChanges: boolean;
  versionCount: number;
  instanceCounts?: { active: number; completed: number; total: number };
  nodeTypes: string[];
  owner: {
    id: number;
    firstName: string;
    lastName: string;
    profileImage: string | null;
  } | null;
}

export interface IWorkflowModuleCounts {
  byModule: Record<string, number>;
  total: number;
}

// ─── Graph read/write (builder ↔ backend) ─────────────────────────────────────

export interface IWorkflowGraphNodeView {
  key: string;
  type: string;
  name: string | null;
  description: string | null;
  config: Record<string, unknown>;
  approverType: string | null;
  approverId: number | null;
}

export interface IWorkflowGraphEdgeView {
  source: string;
  target: string;
  branch: string | null;
  label: string | null;
}

// GET /workflows/templates/:id/graph — the workflow the builder hydrates.
export interface IWorkflowGraphView {
  id: number;
  name: string;
  description: string | null;
  module: string;
  status: WorkflowStatus;
  branchId: number | null;
  fallbackUserId: number | null;
  scopeType: string | null;
  scopeIds: number[];
  eventTypeCode: string | null;
  condition: Record<string, unknown> | null;
  nodes: IWorkflowGraphNodeView[];
  edges: IWorkflowGraphEdgeView[];
  hasUnpublishedChanges: boolean;
  latestVersionNumber: number | null;
  lastPublishedAt: string | null;
}

// POST /workflows/templates/upsert-graph body.
export interface IUpsertWorkflowGraphPayload {
  id?: number;
  name: string;
  description?: string;
  module: string;
  status?: WorkflowStatus;
  publish?: boolean;
  eventTypeCode?: string;
  condition?: Record<string, unknown>;
  scopeType?: string;
  scopeIds?: number[];
  branchId?: number | null;
  fallbackUserId?: number | null;
  nodes: {
    key: string;
    type: string;
    name?: string;
    description?: string;
    config?: Record<string, unknown>;
  }[];
  edges: {
    source: string;
    target: string;
    branch?: string;
    label?: string;
  }[];
}

// POST /workflows/templates/:id/test-step — the builder's "Test" dry-run
// trace. Stateless: the client resends the full decision history each call.
export interface ITestWorkflowDecision {
  nodeKey: string;
  outcome: "approved" | "rejected";
  comments?: string;
}

export interface ITestTraceApprover {
  userId: number;
  name: string;
  role: string | null;
  usedFallback: boolean;
}

export type ITestTraceStep =
  | {
      kind: "auto";
      nodeKey: string;
      nodeType: string;
      nodeName: string | null;
      detail: string;
    }
  | {
      kind: "pending_gate";
      nodeKey: string;
      nodeType: string;
      nodeName: string | null;
      approvers: ITestTraceApprover[];
      rule?: "anyone" | "all";
      warnings: string[];
    }
  | {
      kind: "terminal";
      nodeKey: string;
      nodeName: string | null;
      outcome: "approved" | "rejected" | "cancelled";
    }
  | { kind: "error"; nodeKey?: string; message: string };

export interface ITestTraceResult {
  steps: ITestTraceStep[];
  status: "pending" | "completed" | "error";
}

export interface ITestWorkflowStepPayload {
  requesterId: number;
  sampleData: Record<string, string | number>;
  decisions: ITestWorkflowDecision[];
}

// Backend graph node kind → thumbnail node type. Kinds with no visual (join,
// delay) are dropped from the mini graph.
const NODE_TYPE_TO_THUMBNAIL: Record<string, WorkflowNodeType | null> = {
  start: "trigger",
  approval: "approval",
  parallel_approval: "approval",
  condition: "validation",
  join: null,
  notification: "notification",
  email: "email",
  delay: null,
  escalation: "escalation",
  flow_complete: "end",
  end: "end",
};

const thumbnailNodes = (nodeTypes: string[]): WorkflowNodeType[] =>
  (nodeTypes ?? [])
    .map((type) => NODE_TYPE_TO_THUMBNAIL[type])
    .filter((type): type is WorkflowNodeType => Boolean(type));

const ownerInitials = (owner: IStudioWorkflowListItem["owner"]): string => {
  if (!owner) return "—";
  const initials = `${owner.firstName?.[0] ?? ""}${owner.lastName?.[0] ?? ""}`;
  return initials.toUpperCase() || "—";
};

// Map a backend list item to the card's WorkflowSummary view model.
export const toWorkflowSummary = (
  item: IStudioWorkflowListItem,
  t: TFunction,
): WorkflowSummary => ({
  id: String(item.id),
  title: item.name,
  module: MODULE_CHIP_LABEL[item.module] ?? item.module.toUpperCase(),
  status: item.status,
  scope: item.scopeNames?.length
    ? item.scopeNames.join(" · ")
    : (item.scopeType ?? t("studio:workflows.not_configured")),
  runs: item.instanceCounts?.total ?? 0,
  ownerInitials: ownerInitials(item.owner),
  nodes: thumbnailNodes(item.nodeTypes),
  moduleKey: item.module,
});
